import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Container } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFire,
  faCapsules,
  faChartLine,
  faGraduationCap,
  faFileLines,
  faPlus,
  faPen,
  faTrash,
  faCheck,
  faExclamationTriangle,
  faBell,
  faSpinner,
  faSpa,
  faHeartPulse,
  faBrain,
  faWeightScale,
  faNotesMedical,
  faCalendarAlt,
  faGlassWater,
  faClipboardCheck,
  faCalendarCheck,
  faMoon,
  faFaceSmile,
  faShieldHeart,
  faStethoscope,
  faToggleOn,
  faToggleOff,
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import womensHealthService, {
  MenopauseProfile,
  MenopauseSymptomLog,
  MenopauseWellnessSummary,
  MenopauseMedication,
  AppointmentChecklistItem,
  AppointmentChecklistDefault,
  HydrationChecklistItem,
  HydrationChecklistDefault,
} from '../../../services/womensHealthService';
import BackButton from '../../../components/BackButton/BackButton';
import WHArticleList from '../../../components/WHArticleList/WHArticleList';
import Modal from '../../../components/Modal/Modal';
import ProfileOverviewCard from '../../../components/ProfileOverviewCard/ProfileOverviewCard';
import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog';
import { getApiErrorMessage } from '../../../api/axiosInstance';
import './MenopauseHub.css';
import { useProfile } from '../../../context/ProfileContext';

/* ================================================================== */
/*  Types                                                              */
/* ================================================================== */

type TabKey = 'symptoms' | 'medications' | 'wellness' | 'education' | 'reports';

interface ToastState {
  message: string;
  type: 'success' | 'error' | 'info';
}

interface ChoiceOption {
  value: string;
  display_name: string;
}

interface ChoicesMap {
  [key: string]: ChoiceOption[];
}

/* ================================================================== */
/*  Helpers                                                            */
/* ================================================================== */

const formatDate = (iso: string | null): string => {
  if (!iso) return '--';
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};

const toInputDate = (iso: string | null): string => {
  if (!iso) return '';
  return iso.split('T')[0];
};

const daysBetween = (a: string, b: string): number => {
  const d1 = new Date(a);
  const d2 = new Date(b);
  return Math.round((d2.getTime() - d1.getTime()) / (1000 * 60 * 60 * 24));
};

const SEVERITY_COLORS: Record<string, string> = {
  none: '#94a3b8',
  mild: '#fbbf24',
  moderate: '#f59e0b',
  severe: '#ef4444',
  excellent: '#10b981',
  great: '#10b981',
  good: '#10b981',
  okay: '#94a3b8',
  fair: '#f59e0b',
  poor: '#ef4444',
  happy: '#10b981',
  neutral: '#94a3b8',
  anxious: '#f59e0b',
  irritable: '#f97316',
  sad: '#6366f1',
  low: '#f97316',
  high: '#ef4444',
};

/* ================================================================== */
/*  Toast Component                                                    */
/* ================================================================== */

interface ToastProps {
  message: string;
  type: 'success' | 'error' | 'info';
  onClose: () => void;
}

const Toast: React.FC<ToastProps> = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const iconMap: Record<string, IconDefinition> = {
    success: faCheck,
    error: faExclamationTriangle,
    info: faBell,
  };

  return (
    <div className={`wh-meno-toast wh-meno-toast-${type}`}>
      <span className="wh-meno-toast-icon">
        <FontAwesomeIcon icon={iconMap[type]} />
      </span>
      <span className="wh-meno-toast-message">{message}</span>
      <button className="wh-meno-toast-close" onClick={onClose} aria-label="Close notification">
        {'\u00D7'}
      </button>
    </div>
  );
};

/* ================================================================== */
/*  Skeleton Loader                                                    */
/* ================================================================== */

const SkeletonCards: React.FC = () => (
  <div className="wh-meno-skeleton-grid">
    {[0, 1, 2].map((i) => (
      <div key={i} className="wh-meno-skeleton-card">
        <div className="wh-meno-skeleton-line long" />
        <div className="wh-meno-skeleton-line medium" />
        <div className="wh-meno-skeleton-line short" />
        <div className="wh-meno-skeleton-line medium" />
      </div>
    ))}
  </div>
);

/* ================================================================== */
/*  Initial Form States                                                */
/* ================================================================== */

const INITIAL_PROFILE_FORM = {
  stage: '',
  onset_date: '',
  last_period_date: '',
};

const INITIAL_SYMPTOM_FORM = {
  date: new Date().toISOString().split('T')[0],
  hot_flash_count: 0,
  hot_flash_severity: '',
  mood: '',
  sleep_quality: '',
  joint_pain: '',
  fatigue_level: '',
  weight: '' as string | number,
  notes: '',
};

const INITIAL_MED_FORM = {
  medication_type: '',
  name: '',
  dosage: '',
  frequency: '',
  start_date: new Date().toISOString().split('T')[0],
  end_date: '',
  is_active: true,
  notes: '',
};

/* ================================================================== */
/*  Main Component                                                     */
/* ================================================================== */

const MenopauseHub: React.FC = () => {
  const { t } = useTranslation(['womensHealth', 'common']);
  const { activeProfile } = useProfile();
  const [searchParams] = useSearchParams();

  // Profile state
  const [profile, setProfile] = useState<MenopauseProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileForm, setProfileForm] = useState(INITIAL_PROFILE_FORM);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileChoices, setProfileChoices] = useState<ChoicesMap>({});

  // Data state
  const [symptomLogs, setSymptomLogs] = useState<MenopauseSymptomLog[]>([]);
  const [wellnessSummary, setWellnessSummary] = useState<MenopauseWellnessSummary | null>(null);
  const [medications, setMedications] = useState<MenopauseMedication[]>([]);
  const [symptomChoices, setSymptomChoices] = useState<ChoicesMap>({});
  const [medChoices, setMedChoices] = useState<ChoicesMap>({});

  // UI state
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<TabKey>((searchParams.get('tab') as TabKey) || 'symptoms');
  const [toast, setToast] = useState<ToastState | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ type: 'symptom' | 'medication'; id: number } | null>(null);

  // Symptom form state
  const [symptomForm, setSymptomForm] = useState(INITIAL_SYMPTOM_FORM);
  const [editingSymptomId, setEditingSymptomId] = useState<number | null>(null);
  const [showSymptomForm, setShowSymptomForm] = useState(false);

  // Medication form state
  const [medForm, setMedForm] = useState(INITIAL_MED_FORM);
  const [editingMedId, setEditingMedId] = useState<number | null>(null);
  const [showMedForm, setShowMedForm] = useState(false);


  // Hydration checklist state — keyed by item_key returned by backend so saved state is stable across list edits.
  const [hydrationChecked, setHydrationChecked] = useState<Record<string, boolean>>({});
  const [hydrationItems, setHydrationItems] = useState<HydrationChecklistDefault[]>([]);

  // Appointment checklist state — keyed by item_key returned by backend so saved state is stable across list edits.
  const [appointmentChecked, setAppointmentChecked] = useState<Record<string, boolean>>({});
  const [appointmentItems, setAppointmentItems] = useState<AppointmentChecklistDefault[]>([]);

  // Toast helper
  const showToast = useCallback((message: string, type: ToastState['type']) => {
    setToast({ message, type });
  }, []);

  /* ================================================================ */
  /*  Data Fetching                                                    */
  /* ================================================================ */

  const fetchProfile = useCallback(async () => {
    try {
      setProfileLoading(true);
      const [prof, choices] = await Promise.all([
        womensHealthService.getMenopauseProfileLatest(),
        womensHealthService.getMenopauseProfileChoices(),
      ]);
      setProfile(prof);
      if (choices) setProfileChoices(choices);
    } catch (err) {
      console.error('Failed to fetch menopause profile:', err);
      showToast('Failed to load menopause profile.', 'error');
    } finally {
      setProfileLoading(false);
    }
  }, [showToast]);

  const fetchData = useCallback(async () => {
    if (!profile) return;
    try {
      setLoading(true);
      const [logs, summary, meds, sChoices, mChoices, apptItems, hydrItems] = await Promise.all([
        womensHealthService.getMenopauseSymptomLogs(profile.id),
        womensHealthService.getMenopauseWellnessSummary(profile.id),
        womensHealthService.getMenopauseMedications(profile.id),
        womensHealthService.getMenopauseSymptomLogChoices(),
        womensHealthService.getMenopauseMedicationChoices(),
        womensHealthService.getAppointmentChecklist(profile.id),
        womensHealthService.getHydrationChecklist(profile.id),
      ]);
      setSymptomLogs(Array.isArray(logs) ? logs : []);
      setWellnessSummary(summary);
      setMedications(Array.isArray(meds) ? meds : []);
      if (sChoices) setSymptomChoices(sChoices);
      if (mChoices) setMedChoices(mChoices);
      if (Array.isArray(apptItems)) {
        const next: Record<string, boolean> = {};
        apptItems.forEach((row: AppointmentChecklistItem) => { next[row.item_key] = row.checked; });
        setAppointmentChecked(next);
      }
      if (Array.isArray(hydrItems)) {
        const next: Record<string, boolean> = {};
        hydrItems.forEach((row: HydrationChecklistItem) => { next[row.item_key] = row.checked; });
        setHydrationChecked(next);
      }
    } catch (err) {
      console.error('Failed to fetch menopause data:', err);
      showToast('Failed to load data.', 'error');
    } finally {
      setLoading(false);
    }
  }, [profile, showToast]);

  const toggleAppointmentItem = useCallback((itemKey: string) => {
    setAppointmentChecked((prev) => {
      const nextValue = !prev[itemKey];
      if (profile) {
        womensHealthService
          .saveAppointmentChecklistItem({ menopause_profile: profile.id, item_key: itemKey, checked: nextValue })
          .catch((err) => {
            console.error('Failed to save appointment checklist item:', err);
            showToast('Failed to save checklist update.', 'error');
          });
      }
      return { ...prev, [itemKey]: nextValue };
    });
  }, [profile, showToast]);

  const toggleHydrationItem = useCallback((itemKey: string) => {
    setHydrationChecked((prev) => {
      const nextValue = !prev[itemKey];
      if (profile) {
        womensHealthService
          .saveHydrationChecklistItem({ menopause_profile: profile.id, item_key: itemKey, checked: nextValue })
          .catch((err) => {
            console.error('Failed to save hydration checklist item:', err);
            showToast('Failed to save checklist update.', 'error');
          });
      }
      return { ...prev, [itemKey]: nextValue };
    });
  }, [profile, showToast]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile, activeProfile]);

  useEffect(() => {
    if (profile) fetchData();
  }, [profile, fetchData, activeProfile]);

  useEffect(() => {
    let cancelled = false;
    womensHealthService.getAppointmentChecklistDefaults()
      .then((items) => { if (!cancelled) setAppointmentItems(items); })
      .catch((err) => { console.error('Failed to load appointment checklist defaults:', err); });
    womensHealthService.getHydrationChecklistDefaults()
      .then((items) => { if (!cancelled) setHydrationItems(items); })
      .catch((err) => { console.error('Failed to load hydration checklist defaults:', err); });
    return () => { cancelled = true; };
  }, []);

  /* ================================================================ */
  /*  Profile Setup                                                    */
  /* ================================================================ */

  const handleProfileSave = async () => {
    if (!profileForm.stage) {
      showToast('Please select your menopause stage.', 'error');
      return;
    }
    try {
      setSaving(true);
      const payload: any = { stage: profileForm.stage };
      if (profileForm.onset_date) payload.onset_date = profileForm.onset_date;
      if (profileForm.last_period_date) payload.last_period_date = profileForm.last_period_date;
      if (editingProfile && profile) {
        const updated = await womensHealthService.updateMenopauseProfile(profile.id, payload);
        setProfile(updated);
        setEditingProfile(false);
        showToast('Menopause profile updated successfully!', 'success');
      } else {
        const newProfile = await womensHealthService.createMenopauseProfile(payload);
        setProfile(newProfile);
        showToast('Menopause profile created successfully!', 'success');
      }
    } catch (err) {
      console.error('Failed to save profile:', err);
      showToast('Failed to save profile. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const startEditProfile = () => {
    if (!profile) return;
    setProfileForm({
      stage: profile.stage,
      onset_date: profile.onset_date || '',
      last_period_date: profile.last_period_date || '',
    });
    setEditingProfile(true);
  };

  const cancelEditProfile = () => {
    setEditingProfile(false);
    setProfileForm(INITIAL_PROFILE_FORM);
  };

  /* ================================================================ */
  /*  Symptom Log CRUD                                                 */
  /* ================================================================ */

  const resetSymptomForm = useCallback(() => {
    setSymptomForm(INITIAL_SYMPTOM_FORM);
    setEditingSymptomId(null);
    setShowSymptomForm(false);
  }, []);

  const handleSymptomEdit = (log: MenopauseSymptomLog) => {
    setSymptomForm({
      date: toInputDate(log.date),
      hot_flash_count: log.hot_flash_count,
      hot_flash_severity: log.hot_flash_severity,
      mood: log.mood,
      sleep_quality: log.sleep_quality,
      joint_pain: log.joint_pain,
      fatigue_level: log.fatigue_level,
      weight: log.weight ?? '',
      notes: log.notes || '',
    });
    setEditingSymptomId(log.id);
    setShowSymptomForm(true);
  };

  const handleSymptomSave = async () => {
    if (!symptomForm.date) {
      showToast('Please select a date.', 'error');
      return;
    }
    if (!profile) return;

    try {
      setSaving(true);
      const payload: any = {
        menopause_profile: profile.id,
        date: symptomForm.date,
        hot_flash_count: symptomForm.hot_flash_count,
        hot_flash_severity: symptomForm.hot_flash_severity || undefined,
        mood: symptomForm.mood || undefined,
        sleep_quality: symptomForm.sleep_quality || undefined,
        joint_pain: symptomForm.joint_pain || undefined,
        fatigue_level: symptomForm.fatigue_level || undefined,
        weight: symptomForm.weight !== '' ? Number(symptomForm.weight) : null,
        notes: symptomForm.notes.trim(),
      };

      if (editingSymptomId !== null) {
        await womensHealthService.updateMenopauseSymptomLog(editingSymptomId, payload);
        showToast('Symptom log updated successfully!', 'success');
      } else {
        await womensHealthService.createMenopauseSymptomLog(payload);
        showToast('Symptom log created successfully!', 'success');
      }

      resetSymptomForm();
      await fetchData();
    } catch (err) {
      console.error('Error saving symptom log:', err);
      showToast(getApiErrorMessage(err, 'Failed to save symptom log. Please try again.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSymptomDelete = async (id: number) => {
    try {
      await womensHealthService.deleteMenopauseSymptomLog(id);
      setSymptomLogs((prev) => prev.filter((l) => l.id !== id));
      await fetchData();
      showToast('Symptom log deleted.', 'success');
      setConfirmDelete(null);
    } catch (err) {
      console.error('Error deleting symptom log:', err);
      showToast('Failed to delete symptom log.', 'error');
    }
  };

  /* ================================================================ */
  /*  Medication CRUD                                                  */
  /* ================================================================ */

  const resetMedForm = useCallback(() => {
    setMedForm(INITIAL_MED_FORM);
    setEditingMedId(null);
    setShowMedForm(false);
  }, []);

  const handleMedEdit = (med: MenopauseMedication) => {
    setMedForm({
      medication_type: med.medication_type,
      name: med.name,
      dosage: med.dosage,
      frequency: med.frequency,
      start_date: toInputDate(med.start_date),
      end_date: med.end_date ? toInputDate(med.end_date) : '',
      is_active: med.is_active,
      notes: med.notes || '',
    });
    setEditingMedId(med.id);
    setShowMedForm(true);
  };

  const handleMedSave = async () => {
    if (!medForm.name.trim()) {
      showToast('Please enter the medication name.', 'error');
      return;
    }
    if (!medForm.start_date) {
      showToast('Please select a start date.', 'error');
      return;
    }
    if (!profile) return;

    try {
      setSaving(true);
      const payload: any = {
        menopause_profile: profile.id,
        medication_type: medForm.medication_type || undefined,
        name: medForm.name.trim(),
        dosage: medForm.dosage.trim(),
        frequency: medForm.frequency || undefined,
        start_date: medForm.start_date,
        end_date: medForm.end_date || null,
        is_active: medForm.is_active,
        notes: medForm.notes.trim(),
      };

      if (editingMedId !== null) {
        await womensHealthService.updateMenopauseMedication(editingMedId, payload);
        showToast('Medication updated successfully!', 'success');
      } else {
        await womensHealthService.createMenopauseMedication(payload);
        showToast('Medication added successfully!', 'success');
      }

      resetMedForm();
      await fetchData();
    } catch (err) {
      console.error('Error saving medication:', err);
      showToast('Failed to save medication. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleMedDelete = async (id: number) => {
    try {
      await womensHealthService.deleteMenopauseMedication(id);
      setMedications((prev) => prev.filter((m) => m.id !== id));
      showToast('Medication deleted.', 'success');
      setConfirmDelete(null);
    } catch (err) {
      console.error('Error deleting medication:', err);
      showToast('Failed to delete medication.', 'error');
    }
  };

  /* ================================================================ */
  /*  Computed Wellness Data                                           */
  /* ================================================================ */

  const wellnessStats = wellnessSummary;

  const activeMedCount = useMemo(() => {
    return medications.filter((m) => m.is_active).length;
  }, [medications]);

  const daysSinceLastPeriod = useMemo(() => {
    if (!profile?.last_period_date) return null;
    const today = new Date().toISOString().split('T')[0];
    return daysBetween(profile.last_period_date, today);
  }, [profile]);

  /* ================================================================ */
  /*  Tab Config                                                       */
  /* ================================================================ */

  const tabs: { key: TabKey; label: string; icon: IconDefinition }[] = [
    { key: 'symptoms', label: t('womensHealth:menopause.symptomTracker'), icon: faFire },
    { key: 'medications', label: t('womensHealth:menopause.treatments'), icon: faCapsules },
    { key: 'wellness', label: t('womensHealth:menopause.wellness'), icon: faChartLine },
    { key: 'education', label: t('womensHealth:menopause.education'), icon: faGraduationCap },
    { key: 'reports', label: t('womensHealth:menopause.reports'), icon: faFileLines },
  ];

  /* ================================================================ */
  /*  Render: Profile Setup                                            */
  /* ================================================================ */

  const renderProfileFields = () => {
    const stageChoices: ChoiceOption[] = profileChoices?.stage || [];

    return (
      <>
        <div className="wh-meno-form-group">
          <label className="wh-meno-form-label">
            Menopause Stage <span className="wh-meno-required">*</span>
          </label>
          <select
            className="wh-meno-form-select"
            value={profileForm.stage}
            onChange={(e) => setProfileForm((prev) => ({ ...prev, stage: e.target.value }))}
          >
            <option value="">Select your stage</option>
            {stageChoices.map((c) => (
              <option key={c.value} value={c.value}>{c.display_name}</option>
            ))}
          </select>
        </div>

        <div className="wh-meno-setup-form-row">
          <div className="wh-meno-form-group">
            <label className="wh-meno-form-label">Onset Date (approx.)</label>
            <input
              type="date"
              className="wh-meno-form-input"
              value={profileForm.onset_date}
              onChange={(e) => setProfileForm((prev) => ({ ...prev, onset_date: e.target.value }))}
            />
          </div>
          <div className="wh-meno-form-group">
            <label className="wh-meno-form-label">Last Period Date</label>
            <input
              type="date"
              className="wh-meno-form-input"
              value={profileForm.last_period_date}
              onChange={(e) => setProfileForm((prev) => ({ ...prev, last_period_date: e.target.value }))}
            />
          </div>
        </div>
      </>
    );
  };

  const renderProfileSetup = () => {
    if (profileLoading) {
      return (
        <Container>
          <div className="wh-meno-setup-wrapper">
            <SkeletonCards />
          </div>
        </Container>
      );
    }

    return (
      <Container>
        <div className="wh-meno-setup-wrapper">
          <div className="wh-meno-setup-card">
            <div className="wh-meno-setup-icon-wrap">
              <FontAwesomeIcon icon={faSpa} />
            </div>
            <h2 className="wh-meno-setup-title">Welcome to MenopauseHub</h2>
            <p className="wh-meno-setup-subtitle">
              Let us set up your menopause profile to provide personalized tracking, wellness insights, and educational resources tailored to your stage.
            </p>

            <div className="wh-meno-setup-form">
              {renderProfileFields()}
              <div className="wh-meno-setup-actions">
                <button
                  className="wh-meno-btn wh-meno-btn-primary wh-meno-setup-submit"
                  onClick={handleProfileSave}
                  disabled={saving || !profileForm.stage}
                >
                  {saving ? (
                    <><FontAwesomeIcon icon={faSpinner} spin /> Creating Profile...</>
                  ) : (
                    <><FontAwesomeIcon icon={faCheck} /> Create My Profile</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Container>
    );
  };

  /* ================================================================ */
  /*  Render: Risk Alert                                               */
  /* ================================================================ */

  const renderRiskAlert = () => {
    if (profile?.stage !== 'postmenopause') return null;

    return (
      <div className="wh-meno-risk-alert">
        <div className="wh-meno-risk-alert-icon">
          <FontAwesomeIcon icon={faShieldHeart} />
        </div>
        <div className="wh-meno-risk-alert-content">
          <strong>Post-Menopause Health Awareness</strong>
          <p>
            Being in post-menopause increases your risk for bone density loss and cardiovascular changes.
            Schedule regular bone density screenings and heart health checkups. Visit the Education tab for detailed guidance.
          </p>
        </div>
      </div>
    );
  };

  /* ================================================================ */
  /*  Render: Symptom Log Tab                                          */
  /* ================================================================ */

  const renderSymptomForm = () => {
    const severityChoices: ChoiceOption[] = symptomChoices?.hot_flash_severity || [];
    const moodChoices: ChoiceOption[] = symptomChoices?.mood || [];
    const sleepChoices: ChoiceOption[] = symptomChoices?.sleep_quality || [];
    const jointChoices: ChoiceOption[] = symptomChoices?.joint_pain || [];
    const fatigueChoices: ChoiceOption[] = symptomChoices?.fatigue_level || [];

    return (
      <div className="wh-meno-form-section">
        <div className="wh-meno-form-grid">
          <div className="wh-meno-form-panel">
            <div className="wh-meno-form-panel-title">
              <FontAwesomeIcon icon={faFire} />
              Symptom Details
            </div>

            <div className="wh-meno-form-row-2">
              <div className="wh-meno-form-group">
                <label className="wh-meno-form-label">
                  Date <span className="wh-meno-required">*</span>
                </label>
                <input
                  type="date"
                  className="wh-meno-form-input"
                  value={symptomForm.date}
                  onChange={(e) => setSymptomForm((prev) => ({ ...prev, date: e.target.value }))}
                />
              </div>
              <div className="wh-meno-form-group">
                <label className="wh-meno-form-label">Hot Flash Count</label>
                <input
                  type="number"
                  className="wh-meno-form-input"
                  min={0}
                  value={symptomForm.hot_flash_count}
                  onChange={(e) => setSymptomForm((prev) => ({ ...prev, hot_flash_count: parseInt(e.target.value) || 0 }))}
                />
              </div>
            </div>

            <div className="wh-meno-form-row-2">
              <div className="wh-meno-form-group">
                <label className="wh-meno-form-label">Hot Flash Severity</label>
                <select
                  className="wh-meno-form-select"
                  value={symptomForm.hot_flash_severity}
                  onChange={(e) => setSymptomForm((prev) => ({ ...prev, hot_flash_severity: e.target.value }))}
                >
                  <option value="">Select severity</option>
                  {severityChoices.map((c) => (
                    <option key={c.value} value={c.value}>{c.display_name}</option>
                  ))}
                </select>
              </div>
              <div className="wh-meno-form-group">
                <label className="wh-meno-form-label">Mood</label>
                <select
                  className="wh-meno-form-select"
                  value={symptomForm.mood}
                  onChange={(e) => setSymptomForm((prev) => ({ ...prev, mood: e.target.value }))}
                >
                  <option value="">Select mood</option>
                  {moodChoices.map((c) => (
                    <option key={c.value} value={c.value}>{c.display_name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="wh-meno-form-row-2">
              <div className="wh-meno-form-group">
                <label className="wh-meno-form-label">Sleep Quality</label>
                <select
                  className="wh-meno-form-select"
                  value={symptomForm.sleep_quality}
                  onChange={(e) => setSymptomForm((prev) => ({ ...prev, sleep_quality: e.target.value }))}
                >
                  <option value="">Select quality</option>
                  {sleepChoices.map((c) => (
                    <option key={c.value} value={c.value}>{c.display_name}</option>
                  ))}
                </select>
              </div>
              <div className="wh-meno-form-group">
                <label className="wh-meno-form-label">Joint Pain</label>
                <select
                  className="wh-meno-form-select"
                  value={symptomForm.joint_pain}
                  onChange={(e) => setSymptomForm((prev) => ({ ...prev, joint_pain: e.target.value }))}
                >
                  <option value="">Select level</option>
                  {jointChoices.map((c) => (
                    <option key={c.value} value={c.value}>{c.display_name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="wh-meno-form-row-2">
              <div className="wh-meno-form-group">
                <label className="wh-meno-form-label">Fatigue Level</label>
                <select
                  className="wh-meno-form-select"
                  value={symptomForm.fatigue_level}
                  onChange={(e) => setSymptomForm((prev) => ({ ...prev, fatigue_level: e.target.value }))}
                >
                  <option value="">Select level</option>
                  {fatigueChoices.map((c) => (
                    <option key={c.value} value={c.value}>{c.display_name}</option>
                  ))}
                </select>
              </div>
              <div className="wh-meno-form-group">
                <label className="wh-meno-form-label">Weight (kg)</label>
                <input
                  type="number"
                  className="wh-meno-form-input"
                  min={0}
                  step={0.1}
                  placeholder="Optional"
                  value={symptomForm.weight}
                  onChange={(e) => setSymptomForm((prev) => ({ ...prev, weight: e.target.value }))}
                />
              </div>
            </div>

            <div className="wh-meno-form-group">
              <label className="wh-meno-form-label">Notes</label>
              <textarea
                className="wh-meno-form-textarea"
                placeholder="Any additional observations about today's symptoms..."
                rows={3}
                value={symptomForm.notes}
                onChange={(e) => setSymptomForm((prev) => ({ ...prev, notes: e.target.value }))}
              />
            </div>
          </div>
        </div>

        <div className="wh-meno-form-actions">
          <button
            type="button"
            className="wh-meno-btn wh-meno-btn-cancel"
            onClick={resetSymptomForm}
          >
            {t('common:buttons.cancel')}
          </button>
          <button
            type="button"
            className="wh-meno-btn wh-meno-btn-primary"
            onClick={handleSymptomSave}
            disabled={saving}
          >
            {saving ? (
              <><FontAwesomeIcon icon={faSpinner} spin /> Saving...</>
            ) : editingSymptomId !== null ? (
              <><FontAwesomeIcon icon={faCheck} /> Update Log</>
            ) : (
              <><FontAwesomeIcon icon={faPlus} /> Save Log</>
            )}
          </button>
        </div>
      </div>
    );
  };

  const renderSymptomTab = () => (
    <div className="wh-meno-tab-inner">
      <div className="wh-meno-section-header">
        <div className="wh-meno-section-header-text">
          <h2>
            <FontAwesomeIcon icon={faFire} />
            {showSymptomForm
              ? editingSymptomId !== null ? 'Edit Symptom Log' : 'New Symptom Log'
              : 'Symptom History'}
          </h2>
          <p>
            {showSymptomForm
              ? 'Record your daily symptoms to track patterns over time.'
              : 'Track hot flashes, mood, sleep, joint pain, and fatigue levels.'}
          </p>
        </div>
        {!showSymptomForm && (
          <button
            className="wh-meno-btn wh-meno-btn-primary"
            onClick={() => { resetSymptomForm(); setShowSymptomForm(true); }}
          >
            <FontAwesomeIcon icon={faPlus} /> Log Symptoms
          </button>
        )}
      </div>

      {showSymptomForm ? (
        renderSymptomForm()
      ) : loading ? (
        <SkeletonCards />
      ) : symptomLogs.length === 0 ? (
        <div className="wh-meno-empty-state">
          <div className="wh-meno-empty-icon-wrap">
            <FontAwesomeIcon icon={faFire} />
          </div>
          <h3>No symptom logs yet</h3>
          <p>Start tracking your menopause symptoms to identify patterns and discuss trends with your healthcare provider.</p>
          <button
            className="wh-meno-btn wh-meno-btn-primary"
            onClick={() => { resetSymptomForm(); setShowSymptomForm(true); }}
          >
            <FontAwesomeIcon icon={faPlus} /> Log Your First Symptoms
          </button>
        </div>
      ) : (
        <div className="wh-meno-cards-grid">
          {symptomLogs.map((log, idx) => (
            <div
              key={log.id}
              className="wh-meno-symptom-card"
              style={{ animationDelay: `${idx * 0.06}s` }}
            >
              <div className="wh-meno-symptom-card-top">
                <div className="wh-meno-symptom-card-header">
                  <div className="wh-meno-symptom-date-badge">
                    <FontAwesomeIcon icon={faCalendarAlt} />
                    <span>{formatDate(log.date)}</span>
                  </div>
                  {log.hot_flash_count > 0 && (
                    <span className="wh-meno-flash-badge">
                      <FontAwesomeIcon icon={faFire} />
                      {log.hot_flash_count} flash{log.hot_flash_count !== 1 ? 'es' : ''}
                    </span>
                  )}
                </div>

                <div className="wh-meno-symptom-metrics">
                  {log.hot_flash_severity_display && (
                    <div className="wh-meno-metric-chip">
                      <span className="wh-meno-metric-dot" style={{ background: SEVERITY_COLORS[log.hot_flash_severity] || '#94a3b8' }} />
                      <span className="wh-meno-metric-label">Severity</span>
                      <span className="wh-meno-metric-value">{log.hot_flash_severity_display}</span>
                    </div>
                  )}
                  {log.mood_display && (
                    <div className="wh-meno-metric-chip">
                      <span className="wh-meno-metric-dot" style={{ background: SEVERITY_COLORS[log.mood] || '#94a3b8' }} />
                      <span className="wh-meno-metric-label">Mood</span>
                      <span className="wh-meno-metric-value">{log.mood_display}</span>
                    </div>
                  )}
                  {log.sleep_quality_display && (
                    <div className="wh-meno-metric-chip">
                      <span className="wh-meno-metric-dot" style={{ background: SEVERITY_COLORS[log.sleep_quality] || '#94a3b8' }} />
                      <span className="wh-meno-metric-label">Sleep</span>
                      <span className="wh-meno-metric-value">{log.sleep_quality_display}</span>
                    </div>
                  )}
                  {log.joint_pain_display && (
                    <div className="wh-meno-metric-chip">
                      <span className="wh-meno-metric-dot" style={{ background: SEVERITY_COLORS[log.joint_pain] || '#94a3b8' }} />
                      <span className="wh-meno-metric-label">Joint Pain</span>
                      <span className="wh-meno-metric-value">{log.joint_pain_display}</span>
                    </div>
                  )}
                  {log.fatigue_level_display && (
                    <div className="wh-meno-metric-chip">
                      <span className="wh-meno-metric-dot" style={{ background: SEVERITY_COLORS[log.fatigue_level] || '#94a3b8' }} />
                      <span className="wh-meno-metric-label">Fatigue</span>
                      <span className="wh-meno-metric-value">{log.fatigue_level_display}</span>
                    </div>
                  )}
                  {log.weight !== null && log.weight !== undefined && (
                    <div className="wh-meno-metric-chip">
                      <span className="wh-meno-metric-dot" style={{ background: '#06b6d4' }} />
                      <span className="wh-meno-metric-label">Weight</span>
                      <span className="wh-meno-metric-value">{log.weight} kg</span>
                    </div>
                  )}
                </div>

                {log.notes && (
                  <div className="wh-meno-notes-preview">
                    <FontAwesomeIcon icon={faNotesMedical} />
                    <span>{log.notes}</span>
                  </div>
                )}
              </div>

              <div className="wh-meno-card-actions">
                <button
                  className="wh-meno-action-btn wh-meno-action-edit"
                  onClick={() => handleSymptomEdit(log)}
                  title="Edit log"
                >
                  <FontAwesomeIcon icon={faPen} /> Edit
                </button>
                <button
                  className="wh-meno-action-btn wh-meno-action-delete"
                  onClick={() => setConfirmDelete({ type: 'symptom', id: log.id })}
                  title="Delete log"
                >
                  <FontAwesomeIcon icon={faTrash} /> {t('common:buttons.delete')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  /* ================================================================ */
  /*  Render: Medications Tab                                          */
  /* ================================================================ */

  const renderMedForm = () => {
    const typeChoices: ChoiceOption[] = medChoices?.medication_type || [];
    const freqChoices: ChoiceOption[] = medChoices?.frequency || [];

    return (
      <div className="wh-meno-form-section">
        <div className="wh-meno-form-grid">
          <div className="wh-meno-form-panel">
            <div className="wh-meno-form-panel-title">
              <FontAwesomeIcon icon={faCapsules} />
              Medication Details
            </div>

            <div className="wh-meno-form-row-2">
              <div className="wh-meno-form-group">
                <label className="wh-meno-form-label">Medication Type</label>
                <select
                  className="wh-meno-form-select"
                  value={medForm.medication_type}
                  onChange={(e) => setMedForm((prev) => ({ ...prev, medication_type: e.target.value }))}
                >
                  <option value="">Select type</option>
                  {typeChoices.map((c) => (
                    <option key={c.value} value={c.value}>{c.display_name}</option>
                  ))}
                </select>
              </div>
              <div className="wh-meno-form-group">
                <label className="wh-meno-form-label">
                  Medication Name <span className="wh-meno-required">*</span>
                </label>
                <input
                  type="text"
                  className="wh-meno-form-input"
                  placeholder="e.g., Estradiol, Progesterone"
                  value={medForm.name}
                  onChange={(e) => setMedForm((prev) => ({ ...prev, name: e.target.value }))}
                />
              </div>
            </div>

            <div className="wh-meno-form-row-2">
              <div className="wh-meno-form-group">
                <label className="wh-meno-form-label">Dosage</label>
                <input
                  type="text"
                  className="wh-meno-form-input"
                  placeholder="e.g., 0.5mg, 100mg"
                  value={medForm.dosage}
                  onChange={(e) => setMedForm((prev) => ({ ...prev, dosage: e.target.value }))}
                />
              </div>
              <div className="wh-meno-form-group">
                <label className="wh-meno-form-label">Frequency</label>
                <select
                  className="wh-meno-form-select"
                  value={medForm.frequency}
                  onChange={(e) => setMedForm((prev) => ({ ...prev, frequency: e.target.value }))}
                >
                  <option value="">Select frequency</option>
                  {freqChoices.map((c) => (
                    <option key={c.value} value={c.value}>{c.display_name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="wh-meno-form-row-2">
              <div className="wh-meno-form-group">
                <label className="wh-meno-form-label">
                  Start Date <span className="wh-meno-required">*</span>
                </label>
                <input
                  type="date"
                  className="wh-meno-form-input"
                  value={medForm.start_date}
                  onChange={(e) => setMedForm((prev) => ({ ...prev, start_date: e.target.value }))}
                />
              </div>
              <div className="wh-meno-form-group">
                <label className="wh-meno-form-label">End Date (optional)</label>
                <input
                  type="date"
                  className="wh-meno-form-input"
                  value={medForm.end_date}
                  onChange={(e) => setMedForm((prev) => ({ ...prev, end_date: e.target.value }))}
                />
              </div>
            </div>

            <div className="wh-meno-form-group">
              <label className="wh-meno-form-label">Active Status</label>
              <button
                type="button"
                className={`wh-meno-toggle-btn ${medForm.is_active ? 'active' : ''}`}
                onClick={() => setMedForm((prev) => ({ ...prev, is_active: !prev.is_active }))}
              >
                <FontAwesomeIcon icon={medForm.is_active ? faToggleOn : faToggleOff} />
                {medForm.is_active ? 'Currently Active' : 'Not Active'}
              </button>
            </div>

            <div className="wh-meno-form-group">
              <label className="wh-meno-form-label">Notes</label>
              <textarea
                className="wh-meno-form-textarea"
                placeholder="Side effects, dosage changes, reminders..."
                rows={3}
                value={medForm.notes}
                onChange={(e) => setMedForm((prev) => ({ ...prev, notes: e.target.value }))}
              />
            </div>
          </div>
        </div>

        <div className="wh-meno-form-actions">
          <button
            type="button"
            className="wh-meno-btn wh-meno-btn-cancel"
            onClick={resetMedForm}
          >
            {t('common:buttons.cancel')}
          </button>
          <button
            type="button"
            className="wh-meno-btn wh-meno-btn-primary"
            onClick={handleMedSave}
            disabled={saving}
          >
            {saving ? (
              <><FontAwesomeIcon icon={faSpinner} spin /> Saving...</>
            ) : editingMedId !== null ? (
              <><FontAwesomeIcon icon={faCheck} /> Update Medication</>
            ) : (
              <><FontAwesomeIcon icon={faPlus} /> Add Medication</>
            )}
          </button>
        </div>
      </div>
    );
  };

  const renderMedicationsTab = () => (
    <div className="wh-meno-tab-inner">
      {/* Medications Section */}
      <div className="wh-meno-section-header">
        <div className="wh-meno-section-header-text">
          <h2>
            <FontAwesomeIcon icon={faCapsules} />
            {showMedForm
              ? editingMedId !== null ? 'Edit Medication' : 'Add Medication'
              : 'My Medications'}
          </h2>
          <p>
            {showMedForm
              ? 'Enter your medication details to keep an organized record.'
              : 'Track your menopause-related medications, HRT, and supplements.'}
          </p>
        </div>
        {!showMedForm && (
          <button
            className="wh-meno-btn wh-meno-btn-primary"
            onClick={() => { resetMedForm(); setShowMedForm(true); }}
          >
            <FontAwesomeIcon icon={faPlus} /> Add Medication
          </button>
        )}
      </div>

      {showMedForm ? (
        renderMedForm()
      ) : loading ? (
        <SkeletonCards />
      ) : medications.length === 0 ? (
        <div className="wh-meno-empty-state">
          <div className="wh-meno-empty-icon-wrap">
            <FontAwesomeIcon icon={faCapsules} />
          </div>
          <h3>No medications recorded</h3>
          <p>Keep track of your HRT, supplements, and other menopause-related medications.</p>
          <button
            className="wh-meno-btn wh-meno-btn-primary"
            onClick={() => { resetMedForm(); setShowMedForm(true); }}
          >
            <FontAwesomeIcon icon={faPlus} /> Add Your First Medication
          </button>
        </div>
      ) : (
        <div className="wh-meno-cards-grid">
          {medications.map((med, idx) => (
            <div
              key={med.id}
              className="wh-meno-med-card"
              style={{ animationDelay: `${idx * 0.06}s` }}
            >
              <div className="wh-meno-med-card-top">
                <div className="wh-meno-med-card-header">
                  <div className="wh-meno-med-name-group">
                    <h4 className="wh-meno-med-name">{med.name}</h4>
                    {med.dosage && <span className="wh-meno-med-dosage">{med.dosage}</span>}
                  </div>
                  <span className={`wh-meno-status-badge ${med.is_active ? 'active' : 'inactive'}`}>
                    {med.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>

                <div className="wh-meno-med-details">
                  {med.medication_type_display && (
                    <div className="wh-meno-med-detail-row">
                      <span className="wh-meno-med-detail-label">Type</span>
                      <span className="wh-meno-med-detail-value">{med.medication_type_display}</span>
                    </div>
                  )}
                  {med.frequency_display && (
                    <div className="wh-meno-med-detail-row">
                      <span className="wh-meno-med-detail-label">Frequency</span>
                      <span className="wh-meno-med-detail-value">{med.frequency_display}</span>
                    </div>
                  )}
                  <div className="wh-meno-med-detail-row">
                    <span className="wh-meno-med-detail-label">Started</span>
                    <span className="wh-meno-med-detail-value">{formatDate(med.start_date)}</span>
                  </div>
                  {med.end_date && (
                    <div className="wh-meno-med-detail-row">
                      <span className="wh-meno-med-detail-label">Ended</span>
                      <span className="wh-meno-med-detail-value">{formatDate(med.end_date)}</span>
                    </div>
                  )}
                </div>

                {med.notes && (
                  <div className="wh-meno-notes-preview">
                    <FontAwesomeIcon icon={faNotesMedical} />
                    <span>{med.notes}</span>
                  </div>
                )}
              </div>

              <div className="wh-meno-card-actions">
                <button
                  className="wh-meno-action-btn wh-meno-action-edit"
                  onClick={() => handleMedEdit(med)}
                  title="Edit medication"
                >
                  <FontAwesomeIcon icon={faPen} /> Edit
                </button>
                <button
                  className="wh-meno-action-btn wh-meno-action-delete"
                  onClick={() => setConfirmDelete({ type: 'medication', id: med.id })}
                  title="Delete medication"
                >
                  <FontAwesomeIcon icon={faTrash} /> {t('common:buttons.delete')}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Hydration Checklist Section */}
      <div className="wh-meno-hydration-section">
        <div className="wh-meno-hydration-header">
          <div className="wh-meno-hydration-icon">
            <FontAwesomeIcon icon={faGlassWater} />
          </div>
          <div>
            <h3>Daily Hydration & Wellness Checklist</h3>
            <p>Staying hydrated helps manage hot flashes and overall well-being during menopause.</p>
          </div>
        </div>
        <div className="wh-meno-checklist-grid">
          {hydrationItems.map((item) => {
            const isChecked = !!hydrationChecked[item.item_key];
            return (
              <label
                key={item.item_key}
                className={`wh-meno-checklist-item ${isChecked ? 'checked' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => toggleHydrationItem(item.item_key)}
                />
                <span className="wh-meno-checklist-checkbox">
                  {isChecked && <FontAwesomeIcon icon={faCheck} />}
                </span>
                <span className="wh-meno-checklist-text">{item.label}</span>
              </label>
            );
          })}
        </div>
        <div className="wh-meno-checklist-progress">
          <div className="wh-meno-checklist-progress-bar">
            <div
              className="wh-meno-checklist-progress-fill"
              style={{ width: `${hydrationItems.length ? (hydrationItems.filter((i) => hydrationChecked[i.item_key]).length / hydrationItems.length) * 100 : 0}%` }}
            />
          </div>
          <span className="wh-meno-checklist-progress-text">
            {hydrationItems.filter((i) => hydrationChecked[i.item_key]).length} of {hydrationItems.length} completed
          </span>
        </div>
      </div>
    </div>
  );

  /* ================================================================ */
  /*  Render: Wellness Dashboard Tab                                   */
  /* ================================================================ */

  const renderWellnessTab = () => {
    if (loading) return <SkeletonCards />;

    if (!wellnessStats) {
      return (
        <div className="wh-meno-tab-inner">
          <div className="wh-meno-empty-state">
            <div className="wh-meno-empty-icon-wrap">
              <FontAwesomeIcon icon={faChartLine} />
            </div>
            <h3>No wellness data available</h3>
            <p>Log your symptoms to see trends, averages, and wellness insights here.</p>
            <button
              className="wh-meno-btn wh-meno-btn-primary"
              onClick={() => { setActiveTab('symptoms'); resetSymptomForm(); setShowSymptomForm(true); }}
            >
              <FontAwesomeIcon icon={faPlus} /> Log Symptoms First
            </button>
          </div>
        </div>
      );
    }

    const recentEntryCount = wellnessStats.recent_entry_count;
    const weightTrend = wellnessStats.weight_trend;
    const weightChange = weightTrend.change_kg;
    let weightChangeClass = '';
    if (weightChange !== null && weightChange > 0) weightChangeClass = 'trend-up';
    else if (weightChange !== null && weightChange < 0) weightChangeClass = 'trend-down';
    const weightChangeLabel = weightChange === null || weightChange === 0
      ? (weightTrend.direction === 'stable' ? 'Stable' : '--')
      : `${weightChange > 0 ? '+' : ''}${weightChange} kg`;

    return (
      <div className="wh-meno-tab-inner">
        <div className="wh-meno-section-header">
          <div className="wh-meno-section-header-text">
            <h2>
              <FontAwesomeIcon icon={faChartLine} />
              Wellness Dashboard
            </h2>
            <p>Your symptom trends and wellness summary based on the last 7 logged entries.</p>
          </div>
        </div>

        <div className="wh-meno-wellness-grid">
          {/* Hot Flashes Card */}
          <div className="wh-meno-wellness-card">
            <div className="wh-meno-wellness-card-icon" style={{ background: '#fff7ed', color: '#f59e0b' }}>
              <FontAwesomeIcon icon={faFire} />
            </div>
            <div className="wh-meno-wellness-card-body">
              <span className="wh-meno-wellness-card-label">Avg. Hot Flashes / Day</span>
              <span className="wh-meno-wellness-card-value">
                {wellnessStats.average_hot_flashes_per_day.toFixed(1)}
              </span>
              <span className="wh-meno-wellness-card-sub">
                {recentEntryCount > 0
                  ? `Last ${recentEntryCount} ${recentEntryCount === 1 ? 'entry' : 'entries'}`
                  : 'No entries logged yet'}
              </span>
            </div>
          </div>

          {/* Mood Card */}
          <div className="wh-meno-wellness-card">
            <div className="wh-meno-wellness-card-icon" style={{ background: '#f0fdf4', color: '#10b981' }}>
              <FontAwesomeIcon icon={faFaceSmile} />
            </div>
            <div className="wh-meno-wellness-card-body">
              <span className="wh-meno-wellness-card-label">Most Common Mood</span>
              <span className="wh-meno-wellness-card-value">
                {wellnessStats.most_common_mood ? wellnessStats.most_common_mood.display : '--'}
              </span>
              <span className="wh-meno-wellness-card-sub">
                {wellnessStats.most_common_mood
                  ? `${wellnessStats.most_common_mood.count} of last ${recentEntryCount} ${recentEntryCount === 1 ? 'entry' : 'entries'}`
                  : 'No mood logged yet'}
              </span>
            </div>
          </div>

          {/* Sleep Quality Card */}
          <div className="wh-meno-wellness-card">
            <div className="wh-meno-wellness-card-icon" style={{ background: '#eef2ff', color: '#6366f1' }}>
              <FontAwesomeIcon icon={faMoon} />
            </div>
            <div className="wh-meno-wellness-card-body">
              <span className="wh-meno-wellness-card-label">Avg. Sleep Quality</span>
              <span className="wh-meno-wellness-card-value">
                {wellnessStats.average_sleep_quality.label || '--'}
              </span>
              <span className="wh-meno-wellness-card-sub">
                {wellnessStats.average_sleep_quality.score !== null
                  ? `Score: ${wellnessStats.average_sleep_quality.score.toFixed(1)} / ${wellnessStats.average_sleep_quality.max_score.toFixed(1)}`
                  : 'No sleep logged yet'}
              </span>
            </div>
          </div>

          {/* Weight Trend Card */}
          <div className="wh-meno-wellness-card">
            <div className="wh-meno-wellness-card-icon" style={{ background: '#ecfeff', color: '#06b6d4' }}>
              <FontAwesomeIcon icon={faWeightScale} />
            </div>
            <div className="wh-meno-wellness-card-body">
              <span className="wh-meno-wellness-card-label">Weight Trend</span>
              <span className={`wh-meno-wellness-card-value ${weightChangeClass}`}>{weightChangeLabel}</span>
              <span className="wh-meno-wellness-card-sub">
                {weightTrend.record_count > 0
                  ? `Across ${weightTrend.record_count} ${weightTrend.record_count === 1 ? 'entry' : 'entries'}`
                  : 'No weight logged yet'}
              </span>
            </div>
          </div>
        </div>

        {/* Summary row */}
        <div className="wh-meno-wellness-summary">
          <div className="wh-meno-wellness-summary-item">
            <FontAwesomeIcon icon={faNotesMedical} />
            <div>
              <strong>{wellnessStats.total_symptom_logs}</strong>
              <span>Total Symptom Logs</span>
            </div>
          </div>
          <div className="wh-meno-wellness-summary-item">
            <FontAwesomeIcon icon={faCapsules} />
            <div>
              <strong>{activeMedCount}</strong>
              <span>Active Medications</span>
            </div>
          </div>
          <div className="wh-meno-wellness-summary-item">
            <FontAwesomeIcon icon={faCalendarAlt} />
            <div>
              <strong>{daysSinceLastPeriod !== null ? `${daysSinceLastPeriod}` : '--'}</strong>
              <span>Days Since Last Period</span>
            </div>
          </div>
          <div className="wh-meno-wellness-summary-item">
            <FontAwesomeIcon icon={faSpa} />
            <div>
              <strong>{profile?.stage_display || '--'}</strong>
              <span>Current Stage</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  /* ================================================================ */
  /*  Render: Education Tab                                            */
  /* ================================================================ */

  const renderEducationTab = () => (
    <div className="wh-meno-tab-inner">
      <div className="wh-meno-section-header">
        <div className="wh-meno-section-header-text">
          <h2>
            <FontAwesomeIcon icon={faGraduationCap} />
            Education Center
          </h2>
          <p>
            Evidence-based information to help you understand and manage your menopause journey.
          </p>
        </div>
      </div>

      <WHArticleList
        stage="menopause"
        contentType="weekly_education"
        variant="accordion"
        icon={faGraduationCap}
        accentColor="#8b5cf6"
      />
    </div>
  );

  /* ================================================================ */
  /*  Render: Reports Tab                                              */
  /* ================================================================ */

  const renderReportsTab = () => {
    const sorted = [...symptomLogs].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    const latest = sorted[0] || null;

    return (
      <div className="wh-meno-tab-inner">
        <div className="wh-meno-section-header">
          <div className="wh-meno-section-header-text">
            <h2>
              <FontAwesomeIcon icon={faFileLines} />
              Reports & Appointment Prep
            </h2>
            <p>
              A summary of your menopause health data and a checklist to prepare for your next doctor visit.
            </p>
          </div>
        </div>

        {/* Data Summary */}
        <div className="wh-meno-report-section">
          <h3 className="wh-meno-report-section-title">
            <FontAwesomeIcon icon={faClipboardCheck} />
            Data Summary
          </h3>

          <div className="wh-meno-report-grid">
            <div className="wh-meno-report-card">
              <div className="wh-meno-report-card-header">
                <FontAwesomeIcon icon={faSpa} />
                Profile Overview
              </div>
              <div className="wh-meno-report-card-body">
                <div className="wh-meno-report-row">
                  <span>Stage</span>
                  <strong>{profile?.stage_display || '--'}</strong>
                </div>
                <div className="wh-meno-report-row">
                  <span>Onset Date</span>
                  <strong>{formatDate(profile?.onset_date || null)}</strong>
                </div>
                <div className="wh-meno-report-row">
                  <span>Last Period</span>
                  <strong>{formatDate(profile?.last_period_date || null)}</strong>
                </div>
                <div className="wh-meno-report-row">
                  <span>Days Since Last Period</span>
                  <strong>{daysSinceLastPeriod !== null ? `${daysSinceLastPeriod} days` : '--'}</strong>
                </div>
              </div>
            </div>

            <div className="wh-meno-report-card">
              <div className="wh-meno-report-card-header">
                <FontAwesomeIcon icon={faFire} />
                Latest Symptom Log
              </div>
              <div className="wh-meno-report-card-body">
                {latest ? (
                  <>
                    <div className="wh-meno-report-row">
                      <span>Date</span>
                      <strong>{formatDate(latest.date)}</strong>
                    </div>
                    <div className="wh-meno-report-row">
                      <span>Hot Flashes</span>
                      <strong>{latest.hot_flash_count} ({latest.hot_flash_severity_display || '--'})</strong>
                    </div>
                    <div className="wh-meno-report-row">
                      <span>Mood</span>
                      <strong>{latest.mood_display || '--'}</strong>
                    </div>
                    <div className="wh-meno-report-row">
                      <span>Sleep</span>
                      <strong>{latest.sleep_quality_display || '--'}</strong>
                    </div>
                    <div className="wh-meno-report-row">
                      <span>Joint Pain</span>
                      <strong>{latest.joint_pain_display || '--'}</strong>
                    </div>
                    <div className="wh-meno-report-row">
                      <span>Fatigue</span>
                      <strong>{latest.fatigue_level_display || '--'}</strong>
                    </div>
                    {latest.weight !== null && (
                      <div className="wh-meno-report-row">
                        <span>Weight</span>
                        <strong>{latest.weight} kg</strong>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="wh-meno-report-empty">No symptom logs recorded yet.</p>
                )}
              </div>
            </div>

            <div className="wh-meno-report-card">
              <div className="wh-meno-report-card-header">
                <FontAwesomeIcon icon={faCapsules} />
                Medication Summary
              </div>
              <div className="wh-meno-report-card-body">
                <div className="wh-meno-report-row">
                  <span>Total Medications</span>
                  <strong>{medications.length}</strong>
                </div>
                <div className="wh-meno-report-row">
                  <span>Active Medications</span>
                  <strong>{activeMedCount}</strong>
                </div>
                {medications.filter((m) => m.is_active).slice(0, 3).map((m) => (
                  <div key={m.id} className="wh-meno-report-row">
                    <span>{m.name}</span>
                    <strong>{m.dosage || '--'} / {m.frequency_display || '--'}</strong>
                  </div>
                ))}
                {activeMedCount === 0 && (
                  <p className="wh-meno-report-empty">No active medications.</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Appointment Preparation */}
        <div className="wh-meno-report-section">
          <h3 className="wh-meno-report-section-title">
            <FontAwesomeIcon icon={faStethoscope} />
            Appointment Preparation Checklist
          </h3>
          <p className="wh-meno-report-section-desc">
            Use this checklist to prepare topics for your next healthcare appointment. Check off items you want to discuss.
          </p>

          <div className="wh-meno-appointment-checklist">
            {appointmentItems.map((item) => {
              const isChecked = !!appointmentChecked[item.item_key];
              return (
                <label
                  key={item.item_key}
                  className={`wh-meno-checklist-item ${isChecked ? 'checked' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggleAppointmentItem(item.item_key)}
                  />
                  <span className="wh-meno-checklist-checkbox">
                    {isChecked && <FontAwesomeIcon icon={faCheck} />}
                  </span>
                  <span className="wh-meno-checklist-text">{item.label}</span>
                </label>
              );
            })}
          </div>

          <div className="wh-meno-appointment-cta">
            <div className="wh-meno-appointment-cta-text">
              <FontAwesomeIcon icon={faCalendarCheck} />
              <div>
                <strong>Ready to book your appointment?</strong>
                <span>Schedule a visit with your healthcare provider to discuss your menopause management.</span>
              </div>
            </div>
            <a href="/teleconsultation" className="wh-meno-btn wh-meno-btn-primary">
              <FontAwesomeIcon icon={faCalendarCheck} />
              Book Appointment
            </a>
          </div>
        </div>
      </div>
    );
  };

  /* ================================================================ */
  /*  Render: Tab Content Router                                       */
  /* ================================================================ */

  const renderTabContent = () => {
    switch (activeTab) {
      case 'symptoms': return renderSymptomTab();
      case 'medications': return renderMedicationsTab();
      case 'wellness': return renderWellnessTab();
      case 'education': return renderEducationTab();
      case 'reports': return renderReportsTab();
      default: return null;
    }
  };

  /* ================================================================ */
  /*  Main Render                                                      */
  /* ================================================================ */

  // First-run onboarding (no profile yet) shows a full-page setup
  if (!profile && !profileLoading) {
    return (
      <div className="wh-meno-page">
        {toast && (
          <div className="wh-meno-toast-container">
            <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
          </div>
        )}

        <section className="wh-meno-hero">
          <div className="wh-meno-hero-circle wh-meno-hero-circle-1" />
          <div className="wh-meno-hero-circle wh-meno-hero-circle-2" />
          <Container>
            <BackButton />
            <div className="wh-meno-hero-content">
              <div className="wh-meno-hero-badge">
                <FontAwesomeIcon icon={faSpa} />
                Menopause &amp; Midlife
              </div>
              <h1 className="wh-meno-hero-title">{t('womensHealth:menopause.title')}</h1>
              <p className="wh-meno-hero-subtitle">
                Your comprehensive companion for managing menopause symptoms, medications, and wellness through every stage.
              </p>
            </div>
          </Container>
        </section>

        {renderProfileSetup()}
      </div>
    );
  }

  if (profileLoading) {
    return (
      <div className="wh-meno-page">
        <section className="wh-meno-hero">
          <div className="wh-meno-hero-circle wh-meno-hero-circle-1" />
          <div className="wh-meno-hero-circle wh-meno-hero-circle-2" />
          <Container>
            <BackButton />
            <div className="wh-meno-hero-content">
              <div className="wh-meno-hero-badge">
                <FontAwesomeIcon icon={faSpa} />
                Menopause &amp; Midlife
              </div>
              <h1 className="wh-meno-hero-title">{t('womensHealth:menopause.title')}</h1>
              <p className="wh-meno-hero-subtitle">Loading your menopause profile...</p>
            </div>
          </Container>
        </section>
        <Container>
          <div style={{ padding: '40px 0' }}>
            <SkeletonCards />
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="wh-meno-page">
      {/* Toast */}
      {toast && (
        <div className="wh-meno-toast-container">
          <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
        </div>
      )}

      {/* Delete Entry Confirmation */}
      <ConfirmDialog
        open={confirmDelete !== null}
        title={confirmDelete?.type === 'symptom' ? 'Delete Symptom Log?' : 'Delete Medication?'}
        message={`Are you sure you want to delete this ${confirmDelete?.type === 'symptom' ? 'symptom log entry' : 'medication record'}? This action cannot be undone.`}
        confirmLabel={t('common:buttons.delete')}
        cancelLabel={t('common:buttons.cancel')}
        variant="danger"
        onConfirm={() => {
          if (!confirmDelete) return;
          if (confirmDelete.type === 'symptom') handleSymptomDelete(confirmDelete.id);
          else handleMedDelete(confirmDelete.id);
        }}
        onClose={() => setConfirmDelete(null)}
      />

      {/* Edit Profile Modal */}
      <Modal
        open={editingProfile}
        onClose={cancelEditProfile}
        title="Edit Your Profile"
        icon={faSpa}
        size="md"
        footer={
          <>
            <button
              className="wh-meno-btn wh-meno-btn-cancel"
              onClick={cancelEditProfile}
              disabled={saving}
            >
              {t('common:buttons.cancel')}
            </button>
            <button
              className="wh-meno-btn wh-meno-btn-primary"
              onClick={handleProfileSave}
              disabled={saving || !profileForm.stage}
            >
              {saving ? (
                <><FontAwesomeIcon icon={faSpinner} spin /> Saving...</>
              ) : (
                <><FontAwesomeIcon icon={faCheck} /> Save Changes</>
              )}
            </button>
          </>
        }
      >
        <div className="wh-meno-setup-form">{renderProfileFields()}</div>
      </Modal>

      {/* Hero Banner */}
      <section className="wh-meno-hero">
        <div className="wh-meno-hero-circle wh-meno-hero-circle-1" />
        <div className="wh-meno-hero-circle wh-meno-hero-circle-2" />
        <div className="wh-meno-hero-circle wh-meno-hero-circle-3" />
        <Container>
          <div className="wh-meno-hero-content">
          <BackButton />
            <div className="wh-meno-hero-badge">
              <FontAwesomeIcon icon={faSpa} />
              Menopause &amp; Midlife
            </div>
            <h1 className="wh-meno-hero-title">{t('womensHealth:menopause.title')}</h1>
            <p className="wh-meno-hero-subtitle">
              Track symptoms, manage medications, and access wellness insights tailored to your menopause journey.
              Currently in <strong>{profile?.stage_display || 'Unknown'}</strong> stage.
            </p>
          </div>
        </Container>
      </section>

      {/* Risk Alert */}
      <Container>
        {renderRiskAlert()}
      </Container>

      {/* Profile Overview */}
      <Container>
        <div className="wh-meno-overview-wrap">
          <ProfileOverviewCard
            title="Your Menopause Profile"
            subtitle={profile?.stage_display ? `Currently in ${profile.stage_display} stage` : undefined}
            icon={faSpa}
            accentColor="#f59e0b"
            rows={[
              { label: 'Stage', value: profile?.stage_display || '--' },
              { label: 'Onset Date', value: formatDate(profile?.onset_date || null) },
              { label: 'Last Period', value: formatDate(profile?.last_period_date || null) },
              { label: 'Days Since Last Period', value: daysSinceLastPeriod !== null ? `${daysSinceLastPeriod} days` : '--' },
            ]}
            onEdit={startEditProfile}
            editLabel="Edit"
          />
        </div>
      </Container>

      {/* Main Content */}
      <Container>
        {/* Tabs */}
        <div className="wh-meno-tabs-container">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`wh-meno-tab-btn ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
            >
              <FontAwesomeIcon icon={tab.icon} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="wh-meno-tab-content" key={activeTab}>
          {renderTabContent()}
        </div>
      </Container>
    </div>
  );
};

export default MenopauseHub;
